import { useState } from 'react';
import { Calendar, X, AlertTriangle, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import type { Trip, ItineraryDay } from '../types/itinerary';

interface ChangeDatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  trip: Trip;
  onDatesChanged: () => void;
}

export default function ChangeDatesModal({
  isOpen,
  onClose,
  trip,
  onDatesChanged,
}: ChangeDatesModalProps) {
  const [newStartDate, setNewStartDate] = useState(trip.startDate?.split('T')[0] || '');
  const [newEndDate, setNewEndDate] = useState(trip.endDate?.split('T')?.[0] || '');
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const oldStart = new Date(trip.startDate);
  const oldEnd = new Date(trip.endDate);
  const newStart = new Date(newStartDate);
  const newEnd = new Date(newEndDate);

  const oldDuration = Math.ceil((oldEnd.getTime() - oldStart.getTime()) / (1000 * 60 * 60 * 24));
  const newDuration = newEndDate ? Math.ceil((newEnd.getTime() - newStart.getTime()) / (1000 * 60 * 60 * 24)) : 0;

  const hasChanges = newStartDate !== trip.startDate?.split('T')[0] || newEndDate !== trip.endDate?.split('T')[0];

  const handleConfirm = async () => {
    if (!hasChanges) {
      toast.error('No changes to save');
      return;
    }

    setLoading(true);
    try {
      // Update trip dates
      const { error: tripError } = await supabase
        .from('trips')
        .update({
          start_date: newStartDate,
          end_date: newEndDate,
        })
        .eq('id', trip.id);

      if (tripError) throw tripError;

      // Calculate day difference
      const dayDiff = newDuration - oldDuration;

      if (dayDiff > 0) {
        // Adding days - create new itinerary days
        const existingDays = await supabase
          .from('itinerary_days')
          .select('*')
          .eq('trip_id', trip.id)
          .order('day_number', { ascending: false })
          .limit(1);

        const lastDayNum = existingDays.data?.[0]?.day_number || 0;

        const newDays = [];
        for (let i = 1; i <= dayDiff; i++) {
          const newDate = new Date(newStart);
          newDate.setDate(newStart.getDate() + oldDuration + i - 1);
          
          newDays.push({
            trip_id: trip.id,
            day_number: lastDayNum + i,
            date: newDate.toISOString().split('T')[0],
            title: `Day ${lastDayNum + i}`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }

        if (newDays.length > 0) {
          const { error: daysError } = await supabase
            .from('itinerary_days')
            .insert(newDays);
          
          if (daysError) throw daysError;
        }
      } else if (dayDiff < 0) {
        // Removing days - delete extra days
        const daysToDelete = Math.abs(dayDiff);
        const existingDays = await supabase
          .from('itinerary_days')
          .select('id')
          .eq('trip_id', trip.id)
          .order('day_number', { ascending: false });

        if (existingDays.data && existingDays.data.length > daysToDelete) {
          const dayIdsToDelete = existingDays.data.slice(0, daysToDelete).map(d => d.id);
          
          // First delete items in those days
          await supabase
            .from('itinerary_items')
            .delete()
            .in('day_id', dayIdsToDelete);

          // Then delete the days
          await supabase
            .from('itinerary_days')
            .delete()
            .in('id', dayIdsToDelete);
        }
      }

      toast.success('Trip dates updated successfully!');
      onDatesChanged();
      onClose();
    } catch (error) {
      console.error('Error updating dates:', error);
      toast.error('Failed to update trip dates');
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Calendar className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Change Trip Dates</h2>
              <p className="text-sm text-gray-500">Update your travel dates</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Dates */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-500 mb-2">Current Dates</p>
            <p className="text-lg font-semibold text-gray-900">
              {oldStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              {' - '}
              {oldEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
            <p className="text-sm text-gray-500 mt-1">{oldDuration} days</p>
          </div>

          {/* New Dates */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Start Date
              </label>
              <input
                type="date"
                value={newStartDate}
                onChange={(e) => setNewStartDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New End Date
              </label>
              <input
                type="date"
                value={newEndDate}
                onChange={(e) => setNewEndDate(e.target.value)}
                min={newStartDate}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Impact Summary */}
          {hasChanges && newDuration > 0 && (
            <div className={`p-4 rounded-lg ${newDuration !== oldDuration ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className={`h-5 w-5 mt-0.5 ${newDuration !== oldDuration ? 'text-amber-600' : 'text-gray-500'}`} />
                <div>
                  <p className={`font-medium ${newDuration !== oldDuration ? 'text-amber-800' : 'text-gray-700'}`}>
                    {newDuration > oldDuration 
                      ? `Adding ${newDuration - oldDuration} day(s) to your trip`
                      : newDuration < oldDuration
                      ? `Removing ${oldDuration - newDuration} day(s) from your trip`
                      : 'Duration remains the same'}
                  </p>
                  <p className={`text-sm mt-1 ${newDuration !== oldDuration ? 'text-amber-700' : 'text-gray-500'}`}>
                    {newDuration > oldDuration
                      ? 'New itinerary days will be created for the additional days.'
                      : newDuration < oldDuration
                      ? 'Extra itinerary days and their items will be deleted.'
                      : 'Only the dates will change, itinerary items will be adjusted.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* New Duration Summary */}
          {newDuration > 0 && (
            <div className="flex items-center justify-between p-4 bg-primary-50 rounded-lg">
              <span className="text-sm font-medium text-primary-700">New Duration</span>
              <span className="text-lg font-semibold text-primary-800">{newDuration} days</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            onClick={() => setShowConfirmation(true)}
            disabled={!hasChanges || newDuration <= 0 || loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Apply Changes'}
          </button>
        </div>

        {/* Confirmation Overlay */}
        {showConfirmation && (
          <div className="absolute inset-0 bg-white/95 flex items-center justify-center p-6">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Date Change</h3>
              <p className="text-gray-600 mb-6">
                {newDuration > oldDuration
                  ? `This will add ${newDuration - oldDuration} new day(s) to your itinerary. Continue?`
                  : newDuration < oldDuration
                  ? `This will remove ${oldDuration - newDuration} day(s) and all items in them. Continue?`
                  : 'This will update your trip dates. Continue?'}
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={loading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Confirm
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
